import { commodityStrings } from '@data/commodities';
import { remoteFile } from '@models/Files';
import { remoteFileField, requiredStringField, stringField } from '@utils/forms';
import * as t from 'io-ts';
import * as h from 'tyrann-io';

export const baseCommodity = t.type({
  commodity_no: t.string,
  name_en: t.string,
  name_ja: t.string,
  url: t.string,
  material: t.string,
  usage: t.string,
  explanation: t.string,
  memo: t.string,
  images: t.array(remoteFile),
  hs_code: t.string,
});

export const commodity = t.type({
  id: t.number,
  ...baseCommodity.props,
});

export type Commodity = t.TypeOf<typeof commodity>;

export const commodityQueryOrder = t.string;

export type CommodityQueryOrder = t.TypeOf<typeof commodityQueryOrder>;

export const commodityQuery = t.type({
  user_id: h.omittable(h.number().cast()),
  commodity_no: h.omittable(h.string()),
  name_en: h.omittable(h.string()),
  page: h.omittable(h.number().cast()),
  ordering: h.omittable(commodityQueryOrder),
  created_at_after: h.omittable(h.string()),
  created_at_before: h.omittable(h.string()),
});

export type CommodityQuery = t.TypeOf<typeof commodityQuery>;

export const commodityPages = t.type({
  count: t.number,
  previous: t.union([t.string, t.null]),
  next: t.union([t.string, t.null]),
  results: t.array(commodity),
});

export const commodityCreate = t.type({
  ...baseCommodity.props,
});

export type CommodityCreate = t.TypeOf<typeof commodityCreate>;

export const commodityPatch = commodityCreate;
export type CommodityPatch = t.TypeOf<typeof commodityPatch>;

export const adminCommodityPatch = t.type({
  hs_code: t.string,
});

export type AdminCommodityPatch = t.TypeOf<typeof adminCommodityPatch>;

export const mapCommodityToAdminCommodityPatch = (c: Commodity): AdminCommodityPatch => {
  return {
    hs_code: c.hs_code,
  };
};

export const adminCommodityForm = t.type({
  hs_code: requiredStringField(commodityStrings.hs_code),
});

export type AdminCommodityForm = t.TypeOf<typeof adminCommodityForm>;

export const mapCommodityToAdminCommodityForm: (c: Commodity) => AdminCommodityForm =
  mapCommodityToAdminCommodityPatch;

export const commodityForm = t.type({
  commodity_no: requiredStringField(commodityStrings.commodity_no),
  name_en: requiredStringField(commodityStrings.name_en),
  name_ja: stringField(commodityStrings.name_ja),
  url: stringField(commodityStrings.url),
  material: requiredStringField(commodityStrings.material),
  usage: requiredStringField(commodityStrings.usage),
  explanation: stringField(commodityStrings.explanation, 0, 512),
  memo: stringField(commodityStrings.memo, 0, 512),
  images: h.array(remoteFileField(commodityStrings.images)).max(6, commodityStrings.maxImages),
  hs_code: stringField(commodityStrings.hs_code),
});

export type CommodityForm = t.TypeOf<typeof commodityForm>;

export const commodityCreateInitialValues: t.TypeOf<typeof commodityForm> = {
  commodity_no: '',
  name_en: '',
  name_ja: '',
  url: '',
  material: '',
  usage: '',
  explanation: '',
  memo: '',
  images: [],
  hs_code: '',
};

export const commodityActionStatus = t.union([
  t.literal('edit'),
  t.literal('createSuccess'),
  t.literal('editSuccess'),
]);
